import React, { useState } from "react";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Button } from "../../../components/ui/button";
import { User, HeartPulse, PenTool, ShieldCheck, FileCheck } from "lucide-react";
import OnboardingFormShell, {
  OnboardingFormStep,
} from "../../../components/onboarding/OnboardingFormShell";
import SignatureDialog, {
  SignatureType,
} from "../../../components/onboarding/SignatureDialog";
import { useOnboardingForm } from "../../../hooks/useOnboardingForm";

interface HealthDeclarationData {
  // Your Details
  legalFirstName: string;
  legalLastName: string;
  dateOfBirth: string;
  positionTitle: string;
  mobilePhone: string;
  // Health Questions
  qInjury: string;
  qInjuryDetails: string;
  qManualHandling: string;
  qManualHandlingDetails: string;
  qMedication: string;
  qMedicationDetails: string;
  qAllergies: string;
  qAllergiesDetails: string;
  qSensory: string;
  qSensoryDetails: string;
  qWorkersComp: string;
  qWorkersCompDetails: string;
  qTreatment: string;
  qTreatmentDetails: string;
  qFitness: string;
  qFitnessDetails: string;
  // Declaration & Signature
  fullName: string;
  signatureDate: string;
  signatureType: string;
  signatureData: string;
  agreed: boolean;
}

type QuestionKey = keyof Pick<
  HealthDeclarationData,
  | "qInjury"
  | "qManualHandling"
  | "qMedication"
  | "qAllergies"
  | "qSensory"
  | "qWorkersComp"
  | "qTreatment"
  | "qFitness"
>;

type DetailsKey = keyof Pick<
  HealthDeclarationData,
  | "qInjuryDetails"
  | "qManualHandlingDetails"
  | "qMedicationDetails"
  | "qAllergiesDetails"
  | "qSensoryDetails"
  | "qWorkersCompDetails"
  | "qTreatmentDetails"
  | "qFitnessDetails"
>;

const STEPS: OnboardingFormStep[] = [
  { id: 1, label: "Your Details", icon: User },
  { id: 2, label: "Health Questions", icon: HeartPulse },
  { id: 3, label: "Declaration & Signature", icon: PenTool },
];

const HEALTH_QUESTIONS: { key: QuestionKey; detailsKey: DetailsKey; text: string }[] = [
  {
    key: "qInjury",
    detailsKey: "qInjuryDetails",
    text: "Do you have any pre-existing injury, illness or medical condition that could affect your ability to perform the inherent requirements of your role?",
  },
  {
    key: "qManualHandling",
    detailsKey: "qManualHandlingDetails",
    text: "Do you have any back, neck, shoulder or joint condition that could be aggravated by manual handling, lifting or repetitive tasks?",
  },
  {
    key: "qMedication",
    detailsKey: "qMedicationDetails",
    text: "Are you taking any medication that may cause drowsiness or affect your alertness, coordination, or the safe operation of machinery or vehicles?",
  },
  {
    key: "qAllergies",
    detailsKey: "qAllergiesDetails",
    text: "Do you have any allergies we should be aware of (e.g. medications, latex, dust, insect stings)?",
  },
  {
    key: "qSensory",
    detailsKey: "qSensoryDetails",
    text: "Do you have any vision or hearing impairment that requires aids or workplace adjustments?",
  },
  {
    key: "qWorkersComp",
    detailsKey: "qWorkersCompDetails",
    text: "Have you ever made a workers compensation claim (in NSW or elsewhere)?",
  },
  {
    key: "qTreatment",
    detailsKey: "qTreatmentDetails",
    text: "Are you currently receiving medical treatment, or awaiting surgery, for any condition?",
  },
  {
    key: "qFitness",
    detailsKey: "qFitnessDetails",
    text: "Is there any reason you may not be able to safely perform physical tasks such as lifting, climbing, working at heights, or working in confined spaces?",
  },
];

const initialData: HealthDeclarationData = {
  legalFirstName: "",
  legalLastName: "",
  dateOfBirth: "",
  positionTitle: "",
  mobilePhone: "",
  qInjury: "",
  qInjuryDetails: "",
  qManualHandling: "",
  qManualHandlingDetails: "",
  qMedication: "",
  qMedicationDetails: "",
  qAllergies: "",
  qAllergiesDetails: "",
  qSensory: "",
  qSensoryDetails: "",
  qWorkersComp: "",
  qWorkersCompDetails: "",
  qTreatment: "",
  qTreatmentDetails: "",
  qFitness: "",
  qFitnessDetails: "",
  fullName: "",
  signatureDate: new Date().toISOString().split("T")[0],
  signatureType: "Draw",
  signatureData: "",
  agreed: false,
};

interface HealthDeclarationFormProps {
  /** HR preview mode: renders the hire's submitted form read-only */
  previewRequestId?: string | null;
}

const HealthDeclarationForm: React.FC<HealthDeclarationFormProps> = ({
  previewRequestId,
}) => {
  const {
    formData,
    updateField,
    currentStep,
    setCurrentStep,
    loading,
    saving,
    isCompleted,
    isEditing,
    submittedAt,
    startEditing,
    saveProgress,
    submit,
    isPreview,
  } = useOnboardingForm<HealthDeclarationData>("health-declaration", initialData, {
    previewRequestId,
  });

  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);

  const readOnly = isPreview || (isCompleted && !isEditing);
  const allQuestionsAnswered = HEALTH_QUESTIONS.every(
    (q) => formData[q.key] !== ""
  );
  const canSubmit =
    !!formData.fullName.trim() &&
    !!formData.signatureData &&
    formData.agreed &&
    allQuestionsAnswered;

  const inputClass = "h-11 rounded-xl border-gray-200 bg-gray-50";
  const labelClass = "text-sm font-medium text-gray-900";

  // ─── STEP 1: Your Details ───
  const renderYourDetails = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Your Details</h3>
        <p className="text-sm text-gray-500">
          Your personal details as they appear on your employment records.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className={labelClass}>Legal First Name</label>
          <Input
            placeholder="First name"
            value={formData.legalFirstName}
            onChange={(e) => updateField("legalFirstName", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <label className={labelClass}>Legal Last Name</label>
          <Input
            placeholder="Last name"
            value={formData.legalLastName}
            onChange={(e) => updateField("legalLastName", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <label className={labelClass}>Date of Birth</label>
          <Input
            type="date"
            value={formData.dateOfBirth}
            onChange={(e) => updateField("dateOfBirth", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <label className={labelClass}>Position Title</label>
          <Input
            placeholder="e.g. Scaffolder"
            value={formData.positionTitle}
            onChange={(e) => updateField("positionTitle", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
        <div className="space-y-2 md:col-span-2">
          <label className={labelClass}>Mobile Phone</label>
          <Input
            placeholder="e.g. 0400 000 000"
            value={formData.mobilePhone}
            onChange={(e) => updateField("mobilePhone", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
      </div>
    </div>
  );

  // ─── STEP 2: Health Questions ───
  const renderHealthQuestions = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Health Questions</h3>
        <p className="text-sm text-gray-500">
          Please answer every question honestly. This helps us make any
          reasonable workplace adjustments and keep you safe on site.
        </p>
      </div>

      {HEALTH_QUESTIONS.map((question, index) => (
        <div
          key={question.key}
          className="border border-gray-200 rounded-xl p-4 space-y-3"
        >
          <p className="text-sm font-medium text-gray-900">
            {index + 1}. {question.text}
          </p>
          <div className="flex gap-3">
            {["yes", "no"].map((value) => (
              <label
                key={value}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                  formData[question.key] === value
                    ? "bg-blue-50/50 border-blue-200"
                    : "bg-white border-gray-200 hover:bg-gray-50"
                } ${readOnly ? "pointer-events-none" : ""}`}
              >
                <input
                  type="radio"
                  name={question.key}
                  checked={formData[question.key] === value}
                  onChange={() => updateField(question.key, value)}
                  disabled={readOnly}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-900 capitalize">{value}</span>
              </label>
            ))}
          </div>
          {formData[question.key] === "yes" && (
            <div className="space-y-2">
              <label className={labelClass}>Please provide details</label>
              <Textarea
                placeholder="Provide any relevant details, including how it may affect your work"
                value={formData[question.detailsKey]}
                onChange={(e) => updateField(question.detailsKey, e.target.value)}
                disabled={readOnly}
                className="rounded-xl border-gray-200 bg-gray-50 min-h-[80px]"
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );

  // ─── STEP 3: Declaration & Signature ───
  const renderDeclaration = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Declaration & Signature</h3>
        <p className="text-sm text-gray-500">Read the declaration carefully before signing</p>
      </div>

      <div className="bg-blue-50/50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-700">
            The information you provide is collected for fitness-for-work and
            workplace-adjustment purposes only. It is handled confidentially in
            accordance with the Privacy Act 1988 (Cth) and will only be
            disclosed with your consent or as required by law.
          </p>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex items-start gap-2">
          <FileCheck className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-700">
            I declare that the information I have provided is true and complete.
            I will notify my manager if my health changes in a way that affects
            my ability to perform my work safely. I understand that I may be
            asked to attend a medical assessment if required for the inherent
            requirements of my role.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className={labelClass}>
            Full Legal Name for Signature <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Your full legal name"
            value={formData.fullName}
            onChange={(e) => updateField("fullName", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <label className={labelClass}>Date</label>
          <Input
            type="date"
            value={formData.signatureDate}
            onChange={(e) => updateField("signatureDate", e.target.value)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
      </div>

      {/* Digital Signature */}
      <div className="space-y-2">
        <label className={labelClass}>Digital Signature</label>
        {formData.signatureData ? (
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center gap-3">
              {formData.signatureType === "Draw" ? (
                <img src={formData.signatureData} alt="Signature" className="max-h-10" />
              ) : (
                <span className="font-signature text-2xl">{formData.signatureData}</span>
              )}
            </div>
            {!readOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateField("signatureData", "")}
                className="rounded-lg"
              >
                Clear
              </Button>
            )}
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsSignatureDialogOpen(true)}
            disabled={readOnly}
            className="w-full rounded-xl border-dashed border-gray-300 py-6 gap-2"
          >
            <PenTool className="w-4 h-4" />
            Click to Sign
          </Button>
        )}
      </div>

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.agreed}
          onChange={(e) => updateField("agreed", e.target.checked)}
          disabled={readOnly}
          className="w-4 h-4 rounded border-gray-300 mt-0.5"
        />
        <span className="text-sm text-gray-700">
          I agree to the declaration above and confirm this electronic signature
          is legally binding.
        </span>
      </label>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderYourDetails();
      case 2:
        return renderHealthQuestions();
      case 3:
        return renderDeclaration();
      default:
        return renderYourDetails();
    }
  };

  return (
    <>
      <OnboardingFormShell
        title="Health Declaration"
        steps={STEPS}
        currentStep={currentStep}
        onStepChange={setCurrentStep}
        onSaveProgress={saveProgress}
        onSubmit={submit}
        saving={saving}
        loading={loading}
        submitDisabled={!canSubmit}
        readOnly={readOnly}
        submittedAt={submittedAt}
        onEdit={isPreview ? undefined : startEditing}
        backTo={previewRequestId ? `/my-onboarding?requestId=${previewRequestId}` : undefined}
        isResubmission={isCompleted}
      >
        {renderStepContent()}
      </OnboardingFormShell>

      <SignatureDialog
        open={isSignatureDialogOpen}
        onOpenChange={setIsSignatureDialogOpen}
        onConfirm={(type: SignatureType, data: string) => {
          updateField("signatureType", type);
          updateField("signatureData", data);
        }}
      />
    </>
  );
};

export default HealthDeclarationForm;
